"use client";

import type { LucideIcon } from "lucide-react";
import {
  BookOpen,
  HelpCircle,
  Lock,
  Search,
  Settings,
  ShoppingBag,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useTranslations } from "next-intl"


/**
 * Compact tile for a single Shopify tool (Order Lookup / Catalog / Policies).
 * Designed to be laid out in a 3-column row inside the Shopify Tools section
 * of agent settings. No collapsible body — details are shown in tooltip and
 * the "Configure Instructions" button opens the ToolInstructionsDialog
 * directly.
 *
 * Optional `badge` prop is used to flag the data source (e.g. "Live" for the
 * Storefront MCP tools that fetch fresh data from Shopify on every request).
 */

/**
 * Shared `lockedReason` text for the Order Lookup tile when the tenant has no
 * Shopify integration with `uses_api=true`. Distinct from the Storefront MCP
 * tools (which only need `is_active`) — Order Lookup reads locally-mirrored
 * order data that only API-enabled integrations populate. Surfaced in both
 * email and messaging agent settings so the wording stays consistent.
 */
export function getORDER_LOOKUP_LOCKED_REASON(t: ReturnType<typeof useTranslations<"Metadata.AgentsSharedShopifyToolTile">>): string {
  return t("orderLookupRequiresApi")
}

/** Canonical tool names rendered in the Shopify Tools group. */
export type ShopifyToolName =
  | "get_shopify_order"
  | "search_shopify_catalog"
  | "search_shopify_policies";

/**
 * Static, channel-agnostic presentation metadata for a Shopify tool tile.
 * The dynamic bits (enabled state, toggle/configure handlers, disabled,
 * locked) are supplied per channel; only the copy, icon, tone and badge are
 * shared. Single source of truth consumed by the voice / email / messaging
 * agent settings so the wording can't drift between channels.
 */
export interface ShopifyToolTileDef {
  name: ShopifyToolName;
  icon: LucideIcon;
  title: string;
  tooltip: string;
  tone: ShopifyToolTone;
  badge: ShopifyToolBadge;
  /**
   * Order Lookup reads Admin-API-mirrored data, so it must be locked (not
   * hidden) when no integration has `uses_api`. Consumers compute the actual
   * `lockedReason` from their own access flag + `ORDER_LOOKUP_LOCKED_REASON`.
   */
  requiresApi?: boolean;
}

export function getSHOPIFY_TOOL_TILE_DEFS(t: ReturnType<typeof useTranslations<"Metadata.AgentsSharedShopifyToolTile">>): ShopifyToolTileDef[] {
  return [
  {
    name: "get_shopify_order",
    icon: ShoppingBag,
    title: t("orderLookup"),
    tooltip:
      t("looksUpOrdersByNumberOrCustomerEmail"),
    tone: "default",
    requiresApi: true,
    badge: {
      label: t("connected"),
      variant: "connected",
      tooltip:
        t("readsOrdersSyncedFromYourConnectedShopifyIntegration"),
    },
  },
  {
    name: "search_shopify_catalog",
    icon: Search,
    title: t("catalogSearch"),
    tooltip:
      t("aICanSearchTheStoreSProductCatalog"),
    tone: "live",
    badge: {
      label: t("live"),
      variant: "live",
      tooltip:
        t("fetchesFreshProductDataFromYourShopifyStore"),
    },
  },
  {
    name: "search_shopify_policies",
    icon: BookOpen,
    title: t("policiesFAQs"),
    tooltip:
      t("aICanAnswerQuestionsAboutStorePoliciesReturns"),
    tone: "live",
    badge: {
      label: t("live"),
      variant: "live",
      tooltip:
        t("fetchesPolicyFAQContentLiveFromYourShopify"),
    },
  },
]
}

type ShopifyToolBadgeVariant = "live" | "connected" | "default";
export type ShopifyToolTone = "default" | "live" | "crm";

export interface ShopifyToolBadge {
  label: string;
  tooltip: string;
  variant?: ShopifyToolBadgeVariant;
}

interface ShopifyToolTileProps {
  icon: LucideIcon;
  title: string;
  tooltip: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  onConfigure: () => void;
  disabled?: boolean;
  badge?: ShopifyToolBadge;
  /**
   * Visual tone of the tile background. `"live"` uses sky colors to match the
   * "Live" badge and visually differentiate Storefront MCP tools (Catalog /
   * Policies) from `Order Lookup`, which reads locally-synced data.
   */
  tone?: ShopifyToolTone;
  /**
   * When set, the tile is rendered in a locked state: switch is forced OFF
   * and disabled, the Configure button is disabled, the container is dimmed,
   * and a lock icon is shown next to the title with this string as its
   * tooltip. Used by Order Lookup when the tenant has no active Shopify
   * integration — there is a real upgrade path (connect a store) so we
   * disable rather than hide.
   */
  lockedReason?: string;
}

const BADGE_STYLES: Record<ShopifyToolBadgeVariant, string> = {
  // "Live" — fetches fresh data on every request (Storefront MCP)
  live: "bg-sky-100 text-sky-800 border-sky-300",
  // "Connected" — reads locally-synced data from the tenant's connected
  // Shopify integration (e.g. `shopify_orders` table). Scope is fixed to
  // integration-owned data and is NOT affected by the Shop Domain field.
  connected: "bg-amber-100 text-amber-800 border-amber-300",
  default: "bg-gray-100 text-gray-700 border-gray-300",
};

const TONE_STYLES: Record<ShopifyToolTone, { container: string; icon: string }> = {
  // Default emerald tone — matches the section accent (Order Lookup)
  default: {
    container: "bg-emerald-50 border-emerald-200",
    icon: "text-emerald-600",
  },
  // Live-data tone — sky to match the "Live" badge (Catalog, Policies)
  live: {
    container: "bg-sky-50 border-sky-200",
    icon: "text-sky-600",
  },
  // CRM tone — blue, matches the CRM tools group accent (Update fields,
  // Move stage). Visually distinct from Shopify so the two grouped
  // categories read as siblings rather than the same family.
  crm: {
    container: "bg-blue-50 border-blue-200",
    icon: "text-blue-600",
  },
};

export function ShopifyToolTile({
  icon: Icon,
  title,
  tooltip,
  checked,
  onCheckedChange,
  onConfigure,
  disabled,
  badge,
  tone = "default",
  lockedReason,
}: ShopifyToolTileProps) {
  const tUi = useTranslations("AgentTools.AgentsSharedShopifyToolTile")

  const toneStyles = TONE_STYLES[tone];
  const isLocked = Boolean(lockedReason);
  return (
    // Self-contained provider so this shared tile renders safely in any tree
    // (voice / email / messaging) without each parent supplying a TooltipProvider.
    <TooltipProvider>
    <div
      className={cn(
        "flex flex-col gap-2 p-3 border rounded-lg h-full",
        toneStyles.container,
        isLocked && "opacity-60",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <Icon className={cn("w-4 h-4 shrink-0", toneStyles.icon)} />
          <Label className="text-xs font-medium text-gray-700 truncate">
            {title}
          </Label>
          {badge && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className={cn(
                    "inline-flex items-center rounded-full border px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide leading-none cursor-help shrink-0",
                    BADGE_STYLES[badge.variant ?? "default"],
                  )}
                >
                  {badge.label}
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p>{badge.tooltip}</p>
              </TooltipContent>
            </Tooltip>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <HelpCircle className="w-3.5 h-3.5 text-gray-400 cursor-help shrink-0" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p>{tooltip}</p>
            </TooltipContent>
          </Tooltip>
          {isLocked && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Lock className="w-3.5 h-3.5 text-gray-500 cursor-help shrink-0" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p>{lockedReason}</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        <Switch
          checked={isLocked ? false : checked}
          onCheckedChange={onCheckedChange}
          disabled={disabled || isLocked}
        />
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onConfigure}
        disabled={isLocked}
        className="text-xs w-full mt-auto bg-white"
      >
        <Settings className="w-3 h-3 mr-1.5" />
        <span><span key={tUi("configure")}>{tUi("configure")}</span></span>
      </Button>
    </div>
    </TooltipProvider>
  );
}
